package org.maplibre.compose.sources

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.add
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray

/** A map data source of tiled map pictures. */
public class RasterTileSource : RasterSource {

  private val json: JsonObject

  /**
   * @param id Unique identifier for this source
   * @param uri URI pointing to a JSON file that conforms to the
   *   [TileJSON specification](https://github.com/mapbox/tilejson-spec/)
   * @param tileSize width and height (measured in points) of each tiled image in the raster tile
   *   source. Defaults to 512, the style spec default.
   */
  public constructor(
    id: String,
    uri: String,
    tileSize: Int = 512,
  ) : super(id) {
    json = buildJsonObject {
      put("type", "raster")
      put("url", uri)
      // "tileSize" is one of the few camelCase names in the style spec; "tilesize" is ignored.
      put("tileSize", tileSize)
    }
  }

  /**
   * @param id Unique identifier for this source
   * @param tiles List of URIs pointing to tile images
   * @param from Initial tile settings. See [TileSetOptions]
   * @param tileSize width and height (measured in points) of each tiled image in the raster tile
   *   source. Defaults to 512, the style spec default.
   * @param block Edits [from].
   */
  public constructor(
    id: String,
    tiles: List<String>,
    tileSize: Int = 512,
    from: TileSetOptions = TileSetOptions.Standard,
    block: TileSetOptions.Builder.() -> Unit = {},
  ) : super(id) {
    json = buildJsonObject {
      put("type", "raster")
      putJsonArray("tiles") { tiles.forEach { add(it) } }
      put("tileSize", tileSize)
      putTileSetOptions(TileSetOptions(from, block))
    }
  }

  internal constructor(id: String, definition: JsonObject) : super(id) {
    json = definition
  }

  override fun toJson(): JsonObject = json
}

/** Remember a new [RasterTileSource] with the given [tileSize] from the given [uri]. */
@Composable
public fun rememberRasterTileSource(
  uri: String,
  tileSize: Int = 512,
): RasterTileSource =
  key(uri, tileSize) {
    rememberUserSource { RasterTileSource(id = it, uri = uri, tileSize = tileSize) }
  }

/** Remembers a [RasterTileSource] from [tiles], with settings edited from [from]. */
@Composable
public fun rememberRasterTileSource(
  tiles: List<String>,
  tileSize: Int = 512,
  from: TileSetOptions = TileSetOptions.Standard,
  block: TileSetOptions.Builder.() -> Unit = {},
): RasterTileSource {
  val options = TileSetOptions(from, block)
  return key(tiles, options, tileSize) {
    rememberUserSource {
      RasterTileSource(id = it, tiles = tiles, tileSize = tileSize, from = options)
    }
  }
}
